import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload, Trash2, Building2, CheckCircle2, AlertTriangle, ImageIcon } from "lucide-react";

export default function SchoolProfile() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/school/profile");
      setProfile(data.profile);
    } catch { toast.error("Could not load school profile"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/school/profile", profile);
      setProfile(data.profile);
      toast.success("School profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/school/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile(data.profile);
      toast.success("Logo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload logo"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try { const { data } = await api.delete("/school/profile/logo"); setProfile(data.profile); }
    catch { toast.error("Could not remove logo"); }
  };

  const useDefaultLogo = async () => {
    try { const { data } = await api.post("/school/profile/logo/default"); setProfile(data.profile); toast.success("Using the school's default logo"); }
    catch (e) { toast.error(e.response?.data?.detail || "No default logo available"); }
  };

  if (!profile) return <div className="py-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="school-profile">
      <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${profile.complete ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300" : "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"}`} data-testid="profile-status">
        {profile.complete ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {profile.complete
          ? "School profile is complete — announcements can be published and downloaded."
          : `Incomplete — add: ${profile.missing.map((m) => ({ logo: "Logo", name: "Name", address: "Address", phone: "Phone" }[m] || m)).join(", ")}. These are required to publish or download announcements.`}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Logo */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 bg-white dark:bg-slate-900">
          <Label className="flex items-center gap-2 mb-3"><ImageIcon className="h-4 w-4" /> School Logo <span className="text-rose-500">*</span></Label>
          <div className="aspect-square w-full rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-slate-800/50 mb-3">
            {profile.logo
              ? <img src={profile.logo} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="profile-logo-preview" />
              : <Building2 className="h-12 w-12 text-slate-300" />}
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogo} data-testid="logo-file-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
            </Button>
            {profile.logo && <Button variant="ghost" size="icon" className="rounded-full text-rose-500" onClick={removeLogo} data-testid="remove-logo-btn"><Trash2 className="h-4 w-4" /></Button>}
          </div>
          <p className="text-xs text-slate-400 mt-2">PNG/JPG/WebP. Aspect ratio is preserved automatically.</p>
          <button type="button" onClick={useDefaultLogo} className="text-xs text-sky-600 hover:underline mt-2" data-testid="use-default-logo-btn">Use the school's default logo</button>
        </div>

        {/* Core details */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 bg-white dark:bg-slate-900 space-y-4">
          <div>
            <Label>School Name <span className="text-rose-500">*</span></Label>
            <Input className="mt-1 rounded-lg" value={profile.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" placeholder="Trishul High School, Davanagere" />
          </div>
          <div>
            <Label>Address <span className="text-rose-500">*</span></Label>
            <Textarea className="mt-1 rounded-lg" rows={2} value={profile.address} onChange={(e) => set("address", e.target.value)} data-testid="profile-address" placeholder="Full postal address" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone <span className="text-rose-500">*</span></Label>
              <Input className="mt-1 rounded-lg" value={profile.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" placeholder="+91 XXXXX XXXXX" />
            </div>
            <div>
              <Label>Principal / Signatory name</Label>
              <Input className="mt-1 rounded-lg" value={profile.principal_name} onChange={(e) => set("principal_name", e.target.value)} data-testid="profile-principal" placeholder="Principal" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Email <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input className="mt-1 rounded-lg" value={profile.email} onChange={(e) => set("email", e.target.value)} data-testid="profile-email" />
            </div>
            <div>
              <Label>Website <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input className="mt-1 rounded-lg" value={profile.website} onChange={(e) => set("website", e.target.value)} data-testid="profile-website" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Affiliation No. <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input className="mt-1 rounded-lg" value={profile.affiliation_no} onChange={(e) => set("affiliation_no", e.target.value)} data-testid="profile-affiliation" />
            </div>
            <div>
              <Label>Registration No. <span className="text-xs text-slate-400">(optional)</span></Label>
              <Input className="mt-1 rounded-lg" value={profile.registration_no} onChange={(e) => set("registration_no", e.target.value)} data-testid="profile-registration" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 pt-1">
            <ToggleRow label="Show Email on announcements" checked={profile.show_email} onChange={(v) => set("show_email", v)} testid="toggle-email" />
            <ToggleRow label="Show Website on announcements" checked={profile.show_website} onChange={(v) => set("show_website", v)} testid="toggle-website" />
            <ToggleRow label="Show Affiliation No." checked={profile.show_affiliation} onChange={(v) => set("show_affiliation", v)} testid="toggle-affiliation" />
            <ToggleRow label="Show Registration No." checked={profile.show_registration} onChange={(v) => set("show_registration", v)} testid="toggle-registration" />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Label className="mb-0">Brand accent colour</Label>
            <input type="color" value={profile.brand_color || "#0284c7"} onChange={(e) => set("brand_color", e.target.value)} className="w-9 h-9 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer" data-testid="profile-brand-color" />
          </div>
          <div className="pt-2">
            <Button className="rounded-full px-6" onClick={save} disabled={saving} data-testid="save-profile-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save profile
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
      <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
